// eslint-disable-next-line max-classes-per-file
import {
    IModel,
    IAttribute,
    ModelValidator,
    IConstraint,
    IStringConstraint,
    INumberConstraint,
    IDatatype,
    Collection,
    IRegistry,
    IModelDefinition,
    IAttributeDefinition,
    AcceptedTypesRetriever,
    ITimespanConstraint,
    IAccessControlConstraint
} from './annotation';
import { ExpressionType } from '../expressions/expression';
import 'reflect-metadata';
import { Type } from '@angular/core';

export const TEMPLATE_PROPERTY = '%%template%%';
export const DATAFLOW_TEMPLATE_PROPERTY = '%%dataflowtemplate%%';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
class ModelProxyHandler implements ProxyHandler<any> {
    // eslint-disable-next-line , @typescript-eslint/no-explicit-any
    public set(target: {}, propertyKey: PropertyKey, value: any, receiver: any): boolean {
        if (propertyKey === TEMPLATE_PROPERTY || value === undefined) {
            return Reflect.set(target, propertyKey, value, receiver);
        }
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        if (!Metamodel.isModelOf(target)) {
            return Reflect.set(target, propertyKey, value, receiver);
        }
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        const model = Metamodel.modelOf(target);
        if (!model.hasAttribute(propertyKey as string)) {
            return Reflect.set(target, propertyKey, value, receiver);
        }

        const attribute = model.attribute(propertyKey as string);
        attribute.setTemplateValue(target, undefined);
        return Reflect.set(target, propertyKey, value, receiver);
    }
}

export class Metamodel {
    private static readonly PROXY_HANDLER = new ModelProxyHandler();
    private static readonly ENTITY_KEY = 'entity';
    private static readonly ATTRIBUTE_KEY = 'attribute';

    private static registeredClasses = new Map<string, Function>();

    /*
        Issue with decorators is that property decorators are called before class constructor
        decorator. So we need to hang onto the attribute until the class comes in
    */
    private static registeredAttributes = new Map<Function, Attribute[]>();

    public static registry(annotation: IRegistry): ClassDecorator {
        return <TFunction extends Function>(target: TFunction) => {
            // eslint-disable-next-line no-unused-expressions, @typescript-eslint/no-unused-expressions
            annotation; target;
        };
    }

    public static model(annotation: IModelDefinition): ClassDecorator {
        return <TFunction extends Function>(target: TFunction) => {
            Metamodel.registerModel(target, annotation);
        };
    }

    public static attribute(annotation: IAttributeDefinition): PropertyDecorator {
        return (target: Object, property: string) => {
            const attrType = Reflect.getMetadata('design:type', target, property);
            let datatype: IDatatype | undefined;
            switch (attrType) {
                case String:
                case Date: // Date is actually string in model
                    datatype = String.name;
                    break;
                case Number:
                    datatype = Number.name;
                    break;
                case Boolean:
                    datatype = Boolean.name;
                    break;
                default:
            }

            let annotationType = annotation.type;
            if (!datatype && attrType !== Object && !annotationType) {
                annotationType = () => Metamodel.modelFor(attrType);
            }
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            const attribute = new Attribute(
                property,
                annotation.display || property,
                annotation.reference,
                annotation.transient,
                annotation.collection,
                annotation.constraint,
                datatype !== undefined ? true : annotation.type ? false : undefined,
                annotationType,
                annotation.type2,
                datatype,
                annotation.templatable,
                annotation.accepts,
                annotation.ignoreUndefinedInArray,
                annotation.dynamicAccepts,
                annotation.referenceUpdate,
                annotation.bypassExpressionValidation,
                annotation.defaultValue
            );
            const owner = Metamodel.modelFor(target.constructor);
            attribute._owner = owner as Model;
            if (annotation.association) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (target.constructor as any)[annotation.association] = attribute;
            }

            // Reflect has several defeciences. It does not understand abstract classes
            // since there is no constructor. It does not handle generic types in arrays
            Reflect.defineMetadata(Metamodel.ATTRIBUTE_KEY, attribute, target.constructor, property);
            let attributes = Metamodel.registeredAttributes.get(target.constructor);
            if (!attributes) {
                attributes = [];
                Metamodel.registeredAttributes.set(target.constructor, attributes);
            }
            attributes.push(attribute);
        };
    }

    /**
     * Registers a new model 'dynamically' into the system. Currently it is required
     * to have a base concept model to create a model dynamically
     * <code>
     *  Metamodel.createModel(Dataset.MODEL,
     *  {
     *      name: 'MSDynamics',
     *      displayName: ['Dynamics']
     *  }
     * )
     */
    public static createModel(base: IModel, annotation: IModelDefinition): IModel {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        class Target extends (base.construct as Type<any>) {}

        return Metamodel.registerModel(Target, annotation);
    }

    public static isModel(modelName: string): boolean {
        return Metamodel.registeredClasses.has(modelName.toLowerCase());
    }

    public static isModelOf(object: {}): boolean {
        if (object == null) {
            return false;
        }
        return Metamodel.isModelFor(object.constructor);
    }

    public static isModelFor(construct: Function): boolean {
        return Reflect.getMetadata(Metamodel.ENTITY_KEY, construct);
    }

    /*
       Given a name return its model
    */
    public static modelByName(modelName: string): IModel {
        //models seize case-in-sensitively registered class form here
        const functor = Metamodel.registeredClasses.get(modelName?.toLowerCase());

        if (!functor) {
            throw new Error(modelName + ' not found');
        }
        return Metamodel.modelFor(functor);
    }

    /*
       Given a instance of a object return its IModel
       <code>
            const pipeline = new Pipeline();
            const iModel = Metamodel.modelOf(pipeline);
       </code>
    */
    public static modelOf(object: {}): IModel {
        if (object == null) {
            return undefined;
        }
        return Metamodel.modelFor(object.constructor);
    }

    /*
        Returns the imodel for a constructor
        Metamodel.modelFor(LinkedService);
    */

    public static modelFor(construct: Function): IModel {
        return Reflect.getMetadata(Metamodel.ENTITY_KEY, construct) || undefined;
    }

    /*
        Returns all the models registered. Only use within frameworks
    */
    public static models(): IModel[] {
        const models = [];
        for (const aconstruct of this.registeredClasses.values()) {
            models.push(Metamodel.modelFor(aconstruct));
        }
        return models;
    }

    /*
        Returns the list of all concept models
    */
    public static concepts(): IModel[] {
        // TODO: We are do better with tying IModels together in a hierarchy
        const models = [];
        for (const aconstruct of this.registeredClasses.values()) {
            const model = Metamodel.modelFor(aconstruct);
            if (model.concept === model) {
                models.push(model);
            }
        }
        return models;
    }

    public static proxy<T>(object: {}): T {
        return new Proxy(object, Metamodel.PROXY_HANDLER) as T;
    }

    private static registerModel<TFunction extends Function>(target: TFunction, annotation: IModelDefinition): IModel {
        if (!annotation.name) {
            annotation.name = target.name;
        }
        if (annotation.disabled) {
            annotation.name += '_disabled';
        }
        const superModel = Metamodel.modelFor(Reflect.getPrototypeOf(target.prototype).constructor);
        let reusable: boolean | undefined = annotation.reusable;
        let referable: boolean | undefined = annotation.referable;
        let abstract: boolean | undefined = annotation.abstract;
        let globalScope: boolean | undefined = annotation.globalScope ? annotation.globalScope : reusable;
        let tags = annotation.tags;
        let accessPublishConstraint = annotation.accessPublishConstraint;
        const display =
            annotation.display ||
            (superModel && superModel.display ? superModel.display : [annotation.name, annotation.name]);

        if (superModel) {
            if (!annotation.referable && superModel.referable) {
                referable = true;
            }
            if (!annotation.reusable && superModel.reusable) {
                reusable = true;
            }
            if (annotation.abstract && !superModel.abstract) {
                // We got a problem. We can't throw an error
                abstract = undefined;
            }
            // Remember this is a copy
            const superTags = superModel.tags();
            if (superTags) {
                tags = tags ? { ...superTags, ...tags } : superTags;
            }

            if (!globalScope && superModel.globalScope) {
                globalScope = superModel.globalScope; // Once global always global
            }

            if (!accessPublishConstraint && superModel.accessPublishConstraint) {
                accessPublishConstraint = superModel.accessPublishConstraint;
            }
        }

        const concept = annotation.reusable && !superModel.concept; // Reusable with no supermodel

        let attributes = Metamodel.registeredAttributes.get(target);

        // Add the constraints at the model level with the ones defined at the attributes
        // The model level overrides the ones at the attributes
        if (annotation.constraints) {
            const attributeMap = new Map<string, Attribute>();
            if (attributes) {
                attributes.forEach((attribute) => attributeMap.set(attribute.name, attribute));
            }

            attributes = []; // Clear up we will get new values in

            // Take the model constraints and overlay it on the attribute constraints and come
            // up with a new attribute collection
            for (const attributeName in annotation.constraints) {
                const aconstraint = annotation.constraints[attributeName];
                const attribute = attributeMap.get(attributeName);
                if (attribute) {
                    attributeMap.delete(attributeName); // So we insert the rest back
                    const attributeCopy = attribute.copy(undefined, undefined, undefined, undefined, aconstraint);
                    attributes.push(attributeCopy);
                } else {
                    // Manufacture an attribute
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    attributes.push(
                        new Attribute(
                            attributeName,
                            attributeName,
                            undefined,
                            undefined,
                            undefined,
                            aconstraint,
                            undefined,
                            aconstraint.model
                        )
                    );
                }
            }

            // Whatever is remaining reinsert it back
            attributes.push(...attributeMap.values());
        }
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        const model = new Model(
            target,
            annotation.name ? annotation.name : target.name,
            display,
            reusable,
            referable,
            abstract,
            annotation.reactor,
            concept,
            annotation.transient,
            superModel,
            globalScope,
            annotation.validator,
            attributes,
            tags,
            accessPublishConstraint,
            annotation.createdFromUnknownType
        );

        Reflect.defineMetadata(Metamodel.ENTITY_KEY, model, target);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (target as any)['MODEL'] = model;
        // models were case-in-sensitively registered over here
        Metamodel.registeredClasses.set(model.name.toLowerCase(), target);
        return model;
    }
}

export class Model implements IModel {
    public readonly concept?: IModel;
    public readonly attributes: IAttribute[] = [];
    private readonly attributeMap = new Map<string, Attribute>();

    constructor(
        public readonly construct: Function,
        public readonly name: string,
        public readonly display: string[],
        public readonly reusable?: boolean,
        public readonly referable?: boolean,
        public readonly abstract?: boolean,
        public readonly reactor?: boolean,
        isConcept?: boolean,
        public readonly transient?: boolean,
        public readonly superModel?: IModel,
        public readonly globalScope?: boolean,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        public readonly validator?: ModelValidator,
        attributes?: Attribute[],
        private readonly _tags?: { [tag: string]: {} },
        public readonly accessPublishConstraint?: IAccessControlConstraint,
        public readonly createdFromUnknownType?: boolean
    ) {
        if (isConcept) {
            this.concept = this;
        } else if (superModel && superModel.concept) {
            this.concept = superModel.concept;
        }
        if (superModel) {
            for (const [attrName, attribute] of (superModel as Model).attributeMap) {
                this.attributeMap.set(attrName, attribute);
            }
        }

        if (attributes) {
            for (let attribute of attributes) {
                const superAttribute = this.attributeMap.get(attribute.name);
                if (superAttribute) {
                    const constraint =
                        superAttribute.constraint && attribute.constraint
                            ? { ...{ ...superAttribute.constraint }, ...attribute.constraint }
                            : attribute.constraint;
                    // TODO. does this do overwrite?
                    attribute = superAttribute.copy(
                        attribute.display,
                        attribute.reference,
                        attribute.transient,
                        attribute.collection,
                        constraint,
                        attribute.primitive,
                        attribute.type,
                        attribute.type2,
                        attribute.dType,
                        attribute.templatable,
                        attribute.accepts,
                        attribute.ignoreUndefinedInArray,
                        attribute.referenceUpdate,
                        attribute.bypassExpressionValidation,
                        attribute.defaultValue
                    );
                }
                attribute._owner = this;
                this.attributeMap.set(attribute.name, attribute);
            }
        }

        this.attributeMap.forEach((value) => this.attributes.push(value));
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public create<T>(...parameters: any[]): T {
        if (this.abstract) {
            throw new Error('Cannot create instance of abstract model');
        }

        // ES6 proxy poly fill has issues with 'this'. Prior to proxy
        // this points to the original object but if going through the proxy
        // 'this' resolves to the proxy which can make equality checks a nightmare
        // As of now the assessment is that it works only on immutable objects
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return new (this.construct as any)(...parameters);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public createProxy<T>(...parameters: any[]): T {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return new (this.construct as any)(...parameters);
    }

    /*
        Returns the hierarchy of all models following the super class chain
        <code>
            function isActivity(named: Named){
            const superModels = Metamodel.modelFor(named).superModels();
            for (const superModel for superModels){
                if (superModel.name === 'activity) //Just for illustration
                    return true;
            }
            return false;
            }
        </code>
    */
    public get superModels(): IModel[] {
        const models = [];
        let superModel = this.superModel;
        while (superModel) {
            models.push(superModel);
            superModel = superModel.superModel;
        }

        return models;
    }

    /*
        Queries if this model is a ancestor for the input model
        Metamodel.modelFor(Activity).isAncestor(Metamodel.modelFor(SparkActivity)) === true
    */
    public isAncestor(model: IModel): boolean {
        while (model.superModel) {
            if (model.superModel === this) {
                return true;
            }
            model = model.superModel;
        }
        return false;
    }

    public get subModels(): IModel[] {
        const models = [];
        for (const model of Metamodel.models()) {
            if (this.isAncestor(model)) {
                models.push(model);
            }
        }
        return models;
    }

    public isInstance(object: {}): boolean {
        const model = Metamodel.modelOf(object);
        if (!model) {
            return false;
        }
        return model === this || this.isAncestor(model);
    }

    public hasAttribute(name: string): boolean {
        let aModel: Model | undefined = this;
        while (aModel) {
            if (aModel.attributeMap.has(name)) {
                return true;
            }
            aModel = aModel.superModel as Model;
        }
        return false;
    }

    public attribute(name: string): IAttribute {
        let aModel: Model | undefined = this;
        while (aModel) {
            const attribute = aModel.attributeMap.get(name);
            if (attribute) {
                return attribute;
            }
            aModel = aModel.superModel as Model;
        }

        throw new Error(name + ' not found in this model');
    }

    public tag<T>(name: string): T | undefined {
        return this._tags ? (this._tags[name] as T) : undefined;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public tags(): { [key: string]: any } | undefined {
        return this._tags;
    }
}

export class Attribute implements IAttribute {
    public _owner: Model;

    constructor(
        public name: string,
        public display: string,
        public reference?: boolean | ((context: { inlineDataset?: boolean }) => boolean),
        public transient?: boolean,
        public collection?: Collection,
        public constraint?: IConstraint | IStringConstraint | INumberConstraint | ITimespanConstraint,
        public primitive?: boolean,
        public type?: IModel | (() => IModel),
        public type2?: IModel | (() => IModel),
        public dType?: IDatatype,
        public templatable?: boolean,
        public accepts?: ExpressionType[],
        public ignoreUndefinedInArray?: boolean,
        public dynamicAccepts?: AcceptedTypesRetriever,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        public referenceUpdate?: (value: any) => void,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        public bypassExpressionValidation?: (value: any) => boolean,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        public defaultValue?: any
    ) {}

    public modelType(): IModel {
        if (this.primitive !== false) {
            throw new Error('Not a model');
        }
        return this.type instanceof Function ? this.type() : (this.type as IModel);
    }

    public modelType2(): IModel | undefined {
        return this.type2 ? (this.type2 instanceof Function ? this.type2() : (this.type2 as IModel)) : undefined;
    }

    public datatype(): IDatatype {
        if (!this.dType) {
            throw new Error('Not a primitive type');
        }
        return this.dType;
    }

    public copy(
        display?: string,
        reference?: boolean | ((context: { inlineDataset?: boolean }) => boolean),
        transient?: boolean,
        collection?: Collection,
        constraint?: IConstraint | IStringConstraint | INumberConstraint | ITimespanConstraint,
        primitive?: boolean,
        type?: IModel | (() => IModel),
        type2?: IModel | (() => IModel),
        dType?: IDatatype,
        templatable?: boolean,
        accepts?: ExpressionType[],
        ignoreUndefinedInArray?: boolean,
        referenceUpdate?: this['referenceUpdate'],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        bypassExpressionValidation?: (arg: any) => boolean,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        defaultValue?: any
    ): Attribute {
        return new Attribute(
            this.name,
            display !== undefined ? display : this.display,
            reference !== undefined ? reference : this.reference,
            transient !== undefined ? transient : this.transient,
            collection !== undefined ? collection : this.collection,
            constraint !== undefined ? constraint : this.constraint,
            primitive !== undefined ? primitive : this.primitive,
            type !== undefined ? type : this.type,
            type2 !== undefined ? type2 : this.type2,
            dType !== undefined ? dType : this.dType,
            templatable !== undefined ? templatable : this.templatable,
            accepts !== undefined ? accepts : this.accepts,
            ignoreUndefinedInArray !== undefined ? ignoreUndefinedInArray : this.ignoreUndefinedInArray,
            undefined,
            referenceUpdate ?? this.referenceUpdate,
            bypassExpressionValidation || this.bypassExpressionValidation,
            defaultValue || this.defaultValue
        );
    }

    public get owner(): IModel {
        return this._owner;
    }

    public value<T>(object: {}): T | undefined {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (object as any)[this.name] as T;
    }

    public setValue<T>(object: {}, value: T | undefined, keepTemplateValue: boolean = false): void {
        // Do not delete the attribute unless someone is referring to a required attribute
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (object as any)[this.name] = value;
        if (value !== undefined && !keepTemplateValue) {
            this.setTemplateValue(object, undefined);
        }
    }

    public templateValue(object: {}, templateProp?: string): string | undefined {
        if (object == null) {
            return undefined;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const aObject = object as any;
        const template = aObject[templateProp || TEMPLATE_PROPERTY] as Map<IAttribute, string>;
        if (!template) {
            return undefined;
        }

        // Explicitly return string type. When an object is casted to a Map with generics,
        // the value of a Map is not checked for the correct type.
        const templateVal = template.get(this);
        if (templateVal != null) {
            return templateVal.toString();
        }

        return undefined;
    }

    public setTemplateValue(object: {}, templateValue: string | undefined, templateProp?: string): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const aObject = object as any;
        let template = aObject[templateProp || TEMPLATE_PROPERTY] as Map<IAttribute, string>;
        if (!template && templateValue !== undefined) {
            // Don't unnecesarily create something
            aObject[templateProp || TEMPLATE_PROPERTY] = template = new Map<IAttribute, string>();
        }

        if (templateValue !== undefined) {
            template.set(this, templateValue);
            this.setValue(object, undefined); // Clear the other value
        } else if (template) {
            // Maybe nothing to do
            template.delete(this);
        }
    }

    public isReferenced(context: { inlineDataset?: boolean }): boolean {
        return typeof this.reference === 'function' ? this.reference(context) : this.reference;
    }
}
